import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Droplets, TrendingUp, Link2, Eye, PlayCircle } from "lucide-react";
import { Reveal } from "@/components/od/Reveal";
import { MaskedLine } from "@/components/od/Hero";
import { useSimplify } from "@/components/od/simplify-context";

const SITE = "https://oceandata.ca";

const TOOLS = [
  { name: "Excel", role: "vos chiffriers restent — on arrête la ressaisie" },
  { name: "QuickBooks", role: "votre comptabilité reste — branchée sur le travail réel" },
  { name: "Microsoft 365", role: "vos courriels et fichiers restent — classés au bon endroit" },
  { name: "Google Workspace", role: "vos agendas et documents restent — synchronisés" },
  { name: "Zoho", role: "votre CRM et vos projets restent — enfin connectés" },
  { name: "Autodesk", role: "vos plans restent — liés aux jalons du chantier" },
];

const LEAKS = [
  {
    title: "La ressaisie",
    body: "Une soumission devient un projet, qui devient une facture. Si un humain recopie des chiffres d'un système à l'autre, vous payez deux fois : en minutes, puis en erreurs qui refont surface trois semaines plus tard.",
  },
  {
    title: "La chasse",
    body: "Comptez les messages qui servent seulement à demander où ça en est. Chaque « des nouvelles ? » est une taxe que deux personnes paient. Quand le statut est visible à un seul endroit, la chasse disparaît.",
  },
  {
    title: "La recherche",
    body: "La dernière version du devis, l'ordre de changement signé, la réponse du client — dans combien d'endroits peuvent-ils se cacher ? Chaque réponse de trop, c'est de l'archéologie au lieu du vrai travail.",
  },
  {
    title: "La réunion sur le travail",
    body: "Certaines réunions font avancer le travail. D'autres n'existent que parce que personne ne peut le voir. La réunion du vendredi disparaît dès que le pipeline est simplement visible.",
  },
];

const STEPS = [
  {
    n: "01",
    title: "Montrez-nous",
    body: "Une conversation de 45 minutes sur un seul processus — celui qui fait le plus mal. On cartographie comment le travail voyage vraiment, pas comment il devrait voyager.",
  },
  {
    n: "02",
    title: "On connecte",
    body: "Nous relions les outils que vous avez déjà pour que l'information coule d'elle-même. Votre équipe se pratique d'abord sur une copie sécuritaire. Rien ne change dans ses habitudes.",
  },
  {
    n: "03",
    title: "Vous travaillez",
    body: "30 jours gratuits sur vos vraies données. À la fin, un rapport mesuré : les heures rendues à votre équipe, chiffres à l'appui. Vous décidez ensuite — le rapport reste à vous.",
  },
];

const FAQ = [
  {
    q: "Est-ce que je dois changer de logiciel ?",
    a: "Non. C'est toute l'idée. Nous connectons ce que vous utilisez déjà — Excel, QuickBooks, Microsoft 365, Google, Zoho, Autodesk — pour que l'information circule sans ressaisie. Personne ne réapprend son travail.",
  },
  {
    q: "Combien de temps avant de voir un résultat ?",
    a: "Un premier processus connecté se mesure en semaines, pas en mois. Chez Construction Horizon, le délai de réponse aux RFI a chuté de 43 % en six semaines — mesuré dans leurs propres dossiers.",
  },
  {
    q: "Par où commencer, et à quel prix ?",
    a: "Par une revue gratuite de 15 minutes : vous nous montrez où vont les heures de votre équipe, nous vous montrons où simplifier. Ensuite, un processus à la fois, à prix fixe, avec 30 jours d'essai gratuit avant toute décision.",
  },
];

export default function GardezVosOutilsPage() {
  const { setOpen } = useSimplify();

  useEffect(() => {
    document.title = "Gardez vos outils. On les connecte. | Ocean Data — Montréal";
    document.documentElement.lang = "fr";
    const el = document.createElement("link");
    el.rel = "alternate";
    el.hreflang = "fr-CA";
    el.href = `${SITE}/fr/gardez-vos-outils`;
    el.setAttribute("data-hreflang-injected", "true");
    document.head.appendChild(el);
    return () => {
      document.documentElement.lang = "en";
      el.remove();
    };
  }, []);

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": `${SITE}/fr/gardez-vos-outils`,
        name: "Gardez vos outils. On les connecte.",
        description:
          "Ocean Data connecte les outils que votre entreprise utilise déjà — Excel, QuickBooks, Microsoft 365, Google, Zoho, Autodesk — en un seul système simple, du client au paiement. Sans remplacer vos logiciels.",
        inLanguage: "fr-CA",
        isPartOf: { "@id": `${SITE}/#website` },
        about: { "@id": `${SITE}/#org` },
      },
      {
        "@type": "Service",
        name: "Connexion d'outils existants — simplification des processus",
        provider: { "@id": `${SITE}/#org` },
        areaServed: [
          { "@type": "City", name: "Montréal" },
          { "@type": "AdministrativeArea", name: "Québec" },
        ],
        description:
          "Nous connectons les logiciels que vous possédez déjà en une source unique de vérité pour la construction et les services professionnels : moins de paperasse, moins de ressaisie, moins de chasse à l'information.",
      },
      {
        "@type": "FAQPage",
        inLanguage: "fr-CA",
        mainEntity: FAQ.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  };

  return (
    <main className="pt-16 sm:pt-20 bg-[#F8FAFC]" lang="fr">
      {/* Hero */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-6">
            Montréal · Construction & services professionnels
          </p>
          <h1
            data-testid="gvo-headline"
            className="font-display font-bold tracking-tight leading-[1.06] text-brand-navy text-4xl sm:text-5xl lg:text-6xl max-w-3xl"
          >
            <MaskedLine delay={0.15} testId="gvo-headline-line-1">Gardez vos outils.</MaskedLine>
            <MaskedLine delay={0.3} testId="gvo-headline-line-2">
              <span className="text-brand-teal">On les connecte.</span>
            </MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="mt-7 max-w-xl text-base sm:text-lg text-slate-600 leading-relaxed"
          >
            Vous possédez déjà tout ce qu'il vous faut. Nous relions vos
            logiciels en un seul système simple, du client au paiement. Moins
            de paperasse. Moins de répétition. Moins de courses.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="mt-9 flex flex-col sm:flex-row sm:items-center gap-5"
          >
            <button
              data-testid="gvo-cta-hero"
              onClick={() => setOpen(true)}
              className="inline-flex items-center gap-3 rounded-full bg-brand-navy text-white text-base font-semibold px-9 py-4 transition-all duration-300 hover:bg-brand-teal hover:-translate-y-0.5"
            >
              Montrez-moi où simplifier
              <ArrowRight size={18} />
            </button>
            <p className="text-sm text-slate-500">
              Revue gratuite de 15 minutes · Sans engagement
            </p>
          </motion.div>
        </div>
      </section>

      {/* Le problème n'est pas vos outils */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-5">
            Où les heures s'en vont
          </p>
          <h2 className="font-display font-bold tracking-tight text-brand-navy text-3xl sm:text-4xl max-w-2xl mb-4">
            Le problème n'est pas vos outils. C'est l'espace entre eux.
          </h2>
          <p className="max-w-2xl text-base sm:text-lg text-slate-600 leading-relaxed mb-14">
            On ne remplace rien. On connecte ce qui existe — et les quatre
            fuites ci-dessous se referment d'elles-mêmes.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 lg:gap-7">
            {LEAKS.map((leak, i) => (
              <Reveal key={i} delay={i * 0.07}>
                <div
                  data-testid={`gvo-leak-${i}`}
                  className="rounded-2xl bg-[#F8FAFC] border border-slate-200 p-7 sm:p-8 h-full transition-all duration-300 hover:border-brand-teal hover:-translate-y-0.5"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <span className="w-9 h-9 rounded-full bg-brand-tealsoft text-brand-teal flex items-center justify-center">
                      <Droplets size={16} />
                    </span>
                    <h3 className="font-display font-semibold text-lg text-brand-navy leading-snug">{leak.title}</h3>
                  </div>
                  <p className="text-sm sm:text-base text-slate-600 leading-relaxed">{leak.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Vos outils restent */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-5">
            Ce que vous gardez
          </p>
          <h2 className="font-display font-bold tracking-tight text-brand-navy text-3xl sm:text-4xl max-w-2xl mb-14">
            Vos outils restent. Le double travail s'en va.
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {TOOLS.map((t, i) => (
              <Reveal key={t.name} delay={i * 0.05}>
                <div
                  data-testid={`gvo-tool-${i}`}
                  className="rounded-2xl bg-white border border-slate-200 p-6 h-full transition-all duration-300 hover:border-brand-teal hover:-translate-y-0.5"
                >
                  <div className="flex items-center gap-2.5 mb-2.5">
                    <Link2 size={15} className="text-brand-teal" />
                    <p className="font-display font-semibold text-lg text-brand-navy">{t.name}</p>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed">{t.role}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Comment ça marche */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-5">
            Comment ça marche
          </p>
          <h2 className="font-display font-bold tracking-tight text-brand-navy text-3xl sm:text-4xl max-w-2xl mb-14">
            Trois étapes. Un processus à la fois.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-7">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.08}>
                <div data-testid={`gvo-step-${i}`} className="rounded-2xl bg-[#F8FAFC] border border-slate-200 p-7 sm:p-8 h-full">
                  <p className="font-mono text-sm text-brand-teal font-semibold mb-4">{s.n}</p>
                  <h3 className="font-display font-semibold text-xl text-brand-navy mb-3">{s.title}</h3>
                  <p className="text-sm sm:text-base text-slate-600 leading-relaxed">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Preuve */}
      <section className="py-16 lg:py-24 bg-brand-navy text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-gold font-semibold mb-10">
            Des résultats mesurés, pas des promesses
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 mb-14">
            {[
              ["647", "implémentations Zoho One depuis 2017"],
              ["25–40 %", "de temps administratif en moins, mesuré chez nos clients"],
              ["43 %", "de réponses aux RFI plus rapides chez Construction Horizon"],
            ].map(([num, label], i) => (
              <Reveal key={i} delay={i * 0.07}>
                <div data-testid={`gvo-proof-${i}`}>
                  <p className="font-display font-bold text-4xl sm:text-5xl text-white mb-2">{num}</p>
                  <p className="text-sm text-brand-pale leading-relaxed">{label}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <div className="rounded-2xl bg-brand-navysurf border border-white/10 p-7 sm:p-9 flex flex-col sm:flex-row sm:items-center gap-6 justify-between">
              <div className="flex items-start gap-4 max-w-2xl">
                <TrendingUp size={20} className="text-brand-gold shrink-0 mt-1" />
                <div>
                  <p className="font-display font-medium text-lg leading-snug text-white mb-2">
                    « En six semaines, le délai de réponse de notre équipe aux RFI a chuté de 43 %. Les décisions se prennent maintenant en heures, pas en jours. »
                  </p>
                  <p className="text-xs text-slate-400">Directeur des opérations, Construction Horizon</p>
                </div>
              </div>
              <Link
                to="/fr/journal/horizon-construction-etude-de-cas"
                data-testid="gvo-case-study-link"
                className="shrink-0 inline-flex items-center gap-2 rounded-full border border-brand-gold/50 text-brand-gold text-sm font-semibold px-6 py-3 transition-all duration-300 hover:bg-brand-gold hover:text-brand-navy"
              >
                <Eye size={15} /> Lire l'étude de cas
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 lg:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-8">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-5">
            Questions fréquentes
          </p>
          <h2 className="font-display font-bold tracking-tight text-brand-navy text-3xl sm:text-4xl mb-12">
            Ce qu'on nous demande avant de commencer
          </h2>
          <div className="space-y-4">
            {FAQ.map((f, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <details data-testid={`gvo-faq-${i}`} className="group rounded-2xl bg-white border border-slate-200 px-7 py-6 open:border-brand-teal transition-colors">
                  <summary className="cursor-pointer list-none flex items-center justify-between gap-4 font-display font-semibold text-lg text-brand-navy">
                    {f.q}
                    <span className="text-brand-teal text-xl transition-transform duration-300 group-open:rotate-45">+</span>
                  </summary>
                  <p className="mt-4 text-sm sm:text-base text-slate-600 leading-relaxed">{f.a}</p>
                </details>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA final */}
      <section className="pb-20 lg:pb-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <Reveal>
            <div className="rounded-3xl bg-brand-navy text-white p-10 sm:p-14 flex flex-col lg:flex-row lg:items-center gap-8 justify-between">
              <div className="max-w-xl">
                <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl leading-tight mb-4">
                  Quinze minutes. Vos outils. Une réponse honnête.
                </h2>
                <p className="text-sm sm:text-base text-slate-300 leading-relaxed">
                  Montrez-nous où vont les heures de votre équipe. Nous vous
                  montrons les deux ou trois endroits où simplifier d'abord —
                  sans rien remplacer.
                </p>
              </div>
              <div className="flex flex-col items-start gap-4 shrink-0">
                <button
                  data-testid="gvo-cta-final"
                  onClick={() => setOpen(true)}
                  className="inline-flex items-center gap-3 rounded-full bg-brand-teal text-white text-base font-semibold px-9 py-4 transition-all duration-300 hover:bg-brand-tealhover hover:-translate-y-0.5"
                >
                  <PlayCircle size={18} />
                  Demander ma revue gratuite
                </button>
                <p className="text-xs text-slate-400">Réponse personnelle en un jour ouvrable</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
    </main>
  );
}
